if (!customElements.get('sc-quick-filter')) {
  class ScQuickFilter extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;
      this.onInputChange = this.onInputChange.bind(this);
      this.updateSwiperOverflow = this.updateSwiperOverflow.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onInputChange);

      this.syncItems();
      this.initSwiper();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onInputChange);

      this.destroySwiper();
    }

    get inputs() {
      return Array.from(this.querySelectorAll('[data-sc-filter-input]'));
    }

    onInputChange(event) {
      if (!event.target.matches('[data-sc-filter-input]')) return;

      this.syncItems();

      const searchParams = ScQuickFilter.createSearchParams();

      if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
        FacetFiltersForm.renderPage(searchParams, event);
        return;
      }

      window.location.href = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    }

    renderFromSource(source) {
      const targetWrapper = this.querySelector('.swiper-wrapper');
      const sourceWrapper = source.querySelector('.swiper-wrapper');

      if (!targetWrapper || !sourceWrapper) {
        this.outerHTML = source.outerHTML;
        return;
      }

      const sourceTitle = source.querySelector('.sc-quick-filter__title');
      const targetTitle = this.querySelector('.sc-quick-filter__title');

      this.id = source.id;
      this.className = source.className;
      this.dataset.scFilterGroupKey = source.dataset.scFilterGroupKey;

      if (sourceTitle && targetTitle) targetTitle.innerHTML = sourceTitle.innerHTML;

      this.destroySwiper();

      const orderedOptions = document.createDocumentFragment();

      sourceWrapper.querySelectorAll('[data-sc-filter-option]').forEach((sourceOption) => {
        const optionKey = sourceOption.dataset.scFilterOptionKey;

        if (!optionKey) return;

        orderedOptions.appendChild(sourceOption.cloneNode(true));
      });

      targetWrapper.replaceChildren(orderedOptions);

      this.syncItems();
      this.initSwiper();
    }

    syncItems() {
      this.inputs.forEach((input) => {
        const item = input.closest('.block-swatch')?.querySelector('[data-sc-filter-item]');

        if (item) item.classList.toggle('checked', input.checked);
      });
    }

    initSwiper() {
      const swiperElement = this.querySelector('[data-sc-tabs-swiper]');

      if (!swiperElement) return;

      ScQuickFilter.loadSwiper()
        .then(() => {
          if (!this.isConnected || this.swiper || typeof window.Swiper !== 'function') return;

          this.swiper = new window.Swiper(swiperElement, {
            on: {
              afterInit: this.updateSwiperOverflow,
              resize: this.updateSwiperOverflow,
              update: this.updateSwiperOverflow,
            },
            centeredSlides: false,
            centerInsufficientSlides: false,
            freeMode: {
              enabled: true,
              momentumBounce: false,
            },
            resistanceRatio: 0,
            roundLengths: true,
            slidesPerView: 'auto',
            slidesOffsetAfter: 16,
            spaceBetween: 8,
            watchOverflow: true,
          });

          this.updateSwiperOverflow();
        })
        .catch(() => {});
    }

    updateSwiperOverflow() {
      if (!this.swiper || !this.swiper.el || !this.swiper.wrapperEl) return;

      const hasOverflow = this.swiper.wrapperEl.scrollWidth > this.swiper.el.clientWidth + 1;

      this.swiper.allowTouchMove = hasOverflow;
      this.swiper.el.classList.toggle('sc-quick-filter-swiper--locked', !hasOverflow);

      if (!hasOverflow && typeof this.swiper.setTranslate === 'function') {
        this.swiper.setTranslate(0);
      }
    }

    destroySwiper() {
      if (!this.swiper || typeof this.swiper.destroy !== 'function') return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    static createSearchParams() {
      const searchParams = new URLSearchParams();
      const quickInputs = ScQuickFilter.getQuickFilterInputs();
      const quickParamNames = new Set(quickInputs.map((input) => input.name).filter(Boolean));
      const formIds = ['FacetSortForm', 'FacetSortDrawerForm'];

      formIds.push(document.getElementById('FacetFiltersForm') ? 'FacetFiltersForm' : 'FacetFiltersFormMobile');

      document.querySelectorAll('facet-filters-form form').forEach((form) => {
        if (!formIds.includes(form.id)) return;

        ScQuickFilter.appendFormParams(searchParams, form, quickParamNames);
      });

      quickInputs.forEach((input) => {
        if (!input.name || !input.checked) return;

        searchParams.append(input.name, input.value);
      });

      return searchParams.toString();
    }

    static getQuickFilterInputs() {
      return Array.from(document.querySelectorAll('sc-quick-filter [data-sc-filter-input]'));
    }

    static appendFormParams(searchParams, form, excludedNames) {
      Array.from(form.elements).forEach((element) => {
        if (!element.name || element.disabled || element.closest('.no-js-list')) return;
        if (excludedNames.has(element.name)) return;

        if ((element.type === 'checkbox' || element.type === 'radio') && !element.checked) return;

        if (element.tagName === 'SELECT' && element.multiple) {
          Array.from(element.selectedOptions).forEach((option) => {
            searchParams.append(element.name, option.value);
          });
          return;
        }

        searchParams.append(element.name, element.value);
      });
    }

    static loadSwiper() {
      if (typeof window.Swiper === 'function') return Promise.resolve();
      if (ScQuickFilter.swiperLoadingPromise) return ScQuickFilter.swiperLoadingPromise;

      ScQuickFilter.loadSwiperStyles();

      ScQuickFilter.swiperLoadingPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');

        script.src = ScQuickFilter.swiperScriptUrl;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return ScQuickFilter.swiperLoadingPromise;
    }

    static loadSwiperStyles() {
      if (document.getElementById('sc-quick-filter-swiper-css')) return;

      const link = document.createElement('link');

      link.id = 'sc-quick-filter-swiper-css';
      link.rel = 'stylesheet';
      link.href = ScQuickFilter.swiperStyleUrl;
      document.head.appendChild(link);
    }
  }

  ScQuickFilter.swiperLoadingPromise = null;
  ScQuickFilter.swiperScriptUrl = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
  ScQuickFilter.swiperStyleUrl = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

  customElements.define('sc-quick-filter', ScQuickFilter);
}
